import React, { useEffect, useMemo, useState } from 'react'
import { useDispatch } from 'react-redux'
import { useSelector } from '../../../../Flux/index.js'
import type { State } from '../../../../Flux/Slice/index.js'
import { Actions } from '../../../../Flux/Slice/index.js'
import { visualizationSlice } from '../../../../Flux/Slice/Visualization/VisualizationSlice.js'
import { selectSavableVizState } from './SavedViz.js'
import { SavedViewsStore } from './Storage.js'
import { trackVizTemplateSelected } from '../../../../Services/Telemetry/index.js'
import { DateWidget } from '../../../Common/DateWidget.js'
import { DateTime } from 'luxon'
import { formatDate } from '../../../../Util/DateHandling.js'

// Minimal type for remote list rendering (matches your Kotlin DTO fields we use)
type RemoteItem = {
  id: string
  name: string
  comment?: string | null
  isTemplate: boolean
  createdAt?: string | null
  modifiedAt?: string | null
  schemaVersion: string
}

export default function SavedViewsMenu({ onClose }: { onClose: () => void }) {
  const dispatch = useDispatch()
  const vizActions = Actions[visualizationSlice.name]

  // --- Gather state we need ---------------------------------------------------
  const vizState = useSelector((s: typeof State) => s.visualization)
  const scope = vizState.scope
  const order = useSelector((s: typeof State) => {
    const o = s.order.order
    return o && o !== 'LOADING' && scope?.kind === 'order' && o.id === scope.id ? o : undefined
  })

  const savable = useMemo(() => selectSavableVizState(vizState), [vizState])

  // Remote slice (soft-typed to avoid coupling; adapt names if different)
  const EMPTY_REMOTE_ITEMS: RemoteItem[] = []
  const remoteItems: RemoteItem[] = useSelector((s: typeof State) => s.visualization?.remote?.views?.items) ?? EMPTY_REMOTE_ITEMS
  const remoteLoading: boolean = useSelector((s: typeof State) => s.visualization?.remote?.views?.loading) ?? false
  const remoteError: string | null = useSelector((s: typeof State) => s.visualization?.remote?.views?.error ?? null)

  // --- Local UI state ---------------------------------------------------------
  const [tab, setTab] = useState<'library' | 'remote' | 'export' | 'import'>('library')

  // local section
  const [views, setViews] = useState(() => SavedViewsStore.list())
  const [saveName, setSaveName] = useState('My view')

  // export/import
  const exportText = useMemo(() => JSON.stringify(savable, null, 2), [savable])
  const [importText, setImportText] = useState('')

  // remote section
  const scopeId = scope?.id ?? null
  const [remoteName, setRemoteName] = useState('Shared view')
  const [remoteComment, setRemoteComment] = useState('')
  const [remoteAsTemplate, setRemoteAsTemplate] = useState(false)
  const [remoteShared, setRemoteShared] = useState(false)

  // Load remote list when the modal opens on "remote" tab or tab switches to "remote"
  useEffect(() => {
    if (tab !== 'remote' || !scopeId) return
    dispatch(vizActions.remoteListRequest({ scopeId }))
  }, [tab, scopeId, dispatch, vizActions])

  // --- Local helpers ----------------------------------------------------------
  const refreshLocal = () => setViews(SavedViewsStore.list())

  const handleLocalSave = () => {
    SavedViewsStore.add(saveName || 'Untitled', savable)
    refreshLocal()
    setTab('library')
  }

  const handleLocalApply = (id: string, asTemplate: boolean) => {
    const entry = SavedViewsStore.get(id)
    if (!entry) return
    trackVizTemplateSelected(id, false, order, { source: 'local', as_template: asTemplate })
    dispatch(visualizationSlice.actions.loadSavedViz({ type: 'LoadSavedVizAction', saved: entry.data, asTemplate }))
    onClose()
  }

  const handleLocalDelete = (id: string) => {
    SavedViewsStore.remove(id)
    refreshLocal()
  }

  const handleCopyExport = async () => {
    try {
      await navigator.clipboard.writeText(exportText)
    } catch {
      /* noop */
    }
  }

  const handleImport = () => {
    try {
      const parsed = JSON.parse(importText)
      trackVizTemplateSelected('import', false, order, { source: 'import' })
      dispatch(visualizationSlice.actions.loadSavedViz({ type: 'LoadSavedVizAction', saved: parsed, asTemplate: false }))
      onClose()
    } catch (e) {
      alert('Invalid JSON')

      console.error(e)
    }
  }

  // --- Remote helpers ---------------------------------------------------------
  const handleRemoteSave = () => {
    if (!scope) {
      alert('No scope available to save to.')
      return
    }
    dispatch(
      vizActions.remoteCreateRequest({
        projectId: scope.kind === 'project' ? scope.id : null,
        orderId: scope.kind === 'order' ? scope.id : null,
        name: remoteName || 'Untitled',
        comment: remoteComment || null,
        isTemplate: remoteAsTemplate,
        schemaVersion: savable.version, // 'viz-v1'
        shared: remoteShared,
        state: savable
      })
    )
  }

  const handleRemoteApply = (id: string, asTemplate: boolean) => {
    trackVizTemplateSelected(id, false, order, { source: 'remote', as_template: asTemplate })
    dispatch(vizActions.remoteLoadRequest({ id, asTemplate }))
    onClose()
  }

  const handleRemoteDelete = (id: string, modifiedAt: string) => {
    dispatch(vizActions.remoteDeleteRequest({ id, modifiedAt }))
  }

  const makeShareLink = (id: string, asTemplate = false) => {
    const url = new URL(window.location.href)
    url.searchParams.set('remoteView', id)
    if (asTemplate) url.searchParams.set('asTemplate', '1')
    else url.searchParams.delete('asTemplate')
    return url.toString()
  }

  const copyShareLink = async (id: string, asTemplate = false) => {
    try {
      await navigator.clipboard.writeText(makeShareLink(id, asTemplate))
    } catch {
      /* noop */
    }
  }

  return (
    <>
      <div className="modal modal-open">
        <div className="modal-box max-w-5xl">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-bold text-lg">Saved Views</h3>
            <button className="btn btn-sm btn-ghost" onClick={() => onClose()}>
              Close
            </button>
          </div>

          <div className="tabs mb-4">
            <a className={`tab tab-bordered ${tab === 'library' ? 'tab-active' : ''}`} onClick={() => setTab('library')}>
              Local
            </a>
            <a className={`tab tab-bordered ${tab === 'remote' ? 'tab-active' : ''}`} onClick={() => setTab('remote')}>
              Remote (share)
            </a>
            <a className={`tab tab-bordered ${tab === 'export' ? 'tab-active' : ''}`} onClick={() => setTab('export')}>
              Export
            </a>
            <a className={`tab tab-bordered ${tab === 'import' ? 'tab-active' : ''}`} onClick={() => setTab('import')}>
              Import
            </a>
          </div>

          {tab === 'library' && (
            <div className="space-y-4">
              <div className="flex gap-2 items-end">
                <div className="form-control">
                  <label className="label">
                    <span className="label-text">Save current view locally as</span>
                  </label>
                  <input className="input input-bordered w-80" value={saveName} onChange={(e) => setSaveName(e.target.value)} />
                </div>
                <button className="btn btn-primary" onClick={handleLocalSave}>
                  Save
                </button>
              </div>

              <div className="divider">Saved</div>
              {views.length === 0 ? (
                <div className="opacity-70 text-sm">No local saved views yet.</div>
              ) : (
                <ul className="space-y-2">
                  {views.map((v) => (
                    <li key={v.id} className="p-3 rounded border flex items-center justify-between bg-base-100">
                      <div>
                        <div className="font-medium">{v.name}</div>
                        <div className="text-xs opacity-70">{formatDate(DateTime.fromMillis(v.savedAt))}</div>
                      </div>
                      <div className="flex gap-2">
                        <button className="btn btn-xs" onClick={() => handleLocalApply(v.id, false)}>
                          Apply
                        </button>
                        <button className="btn btn-xs btn-outline" onClick={() => handleLocalApply(v.id, true)}>
                          Apply as Template
                        </button>
                        <button className="btn btn-xs btn-error" onClick={() => handleLocalDelete(v.id)}>
                          Delete
                        </button>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

          {tab === 'remote' && (
            <div className="space-y-5">
              <div className="flex flex-wrap items-end gap-3">
                <div className="form-control">
                  <label className="label">
                    <span className="label-text">Name</span>
                  </label>
                  <input className="input input-bordered w-72" value={remoteName} onChange={(e) => setRemoteName(e.target.value)} />
                </div>
                <div className="form-control">
                  <label className="label">
                    <span className="label-text">Comment</span>
                  </label>
                  <input className="input input-bordered w-96" value={remoteComment} onChange={(e) => setRemoteComment(e.target.value)} />
                </div>
                <label className="label cursor-pointer gap-2">
                  <span className="label-text">Save as template</span>
                  <input
                    type="checkbox"
                    className="toggle"
                    checked={remoteAsTemplate}
                    onChange={(e) => setRemoteAsTemplate(e.target.checked)}
                  />
                </label>
                <label className="label cursor-pointer gap-2">
                  <span className="label-text">Share</span>
                  <input type="checkbox" className="toggle" checked={remoteShared} onChange={(e) => setRemoteShared(e.target.checked)} />
                </label>
                <button className="btn btn-primary" disabled={!scopeId} onClick={handleRemoteSave}>
                  Save to server
                </button>
              </div>

              <div className="divider">Remote views for this scope</div>
              {remoteLoading && <div className="text-sm opacity-70">Loading…</div>}
              {remoteError && <div className="text-sm text-error">Error: {remoteError}</div>}
              {!remoteLoading && remoteItems.length === 0 ? (
                <div className="opacity-70 text-sm">No remote views yet.</div>
              ) : (
                <ul className="space-y-2">
                  {remoteItems.map((it) => (
                    <li key={it.id} className="p-3 rounded border flex items-center justify-between bg-base-100">
                      <div>
                        <div className="font-medium">{it.name}</div>
                        <div className="text-xs opacity-70 flex gap-3">
                          <span>{it.isTemplate ? 'Template' : 'View'}</span>
                          {it.createdAt && <span>created <DateWidget date={it.createdAt} /></span>}
                          {it.modifiedAt && <span>updated <DateWidget date={it.modifiedAt} /></span>}
                          <span className="opacity-60">{it.schemaVersion}</span>
                        </div>
                        {it.comment && <div className="text-xs opacity-80 mt-1">{it.comment}</div>}
                      </div>
                      <div className="flex gap-2">
                        <button className="btn btn-xs" onClick={() => handleRemoteApply(it.id, false)}>
                          Apply
                        </button>
                        <button className="btn btn-xs btn-outline" onClick={() => handleRemoteApply(it.id, true)}>
                          Apply as Template
                        </button>
                        <button className="btn btn-xs btn-ghost" onClick={() => copyShareLink(it.id, false)} title="Copy link">
                          Copy link
                        </button>
                        <button className="btn btn-xs btn-ghost" onClick={() => copyShareLink(it.id, true)} title="Copy link as template">
                          Copy template link
                        </button>
                        <button className="btn btn-xs btn-error" onClick={() => handleRemoteDelete(it.id, it.modifiedAt ?? '')}>
                          Delete
                        </button>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

          {tab === 'export' && (
            <div className="space-y-3">
              <div className="text-sm opacity-70">This JSON captures sources, sinks, shared options (no raw data).</div>
              <textarea className="textarea textarea-bordered w-full h-64 font-mono text-xs" readOnly value={exportText} />
              <div className="flex gap-2">
                <button className="btn btn-sm" onClick={handleCopyExport}>
                  Copy to clipboard
                </button>
                <a
                  className="btn btn-sm btn-outline"
                  download={`viz-view-${Date.now()}.json`}
                  href={`data:application/json;charset=utf-8,${encodeURIComponent(exportText)}`}
                >
                  Download .json
                </a>
              </div>
            </div>
          )}

          {tab === 'import' && (
            <div className="space-y-3">
              <textarea
                className="textarea textarea-bordered w-full h-64 font-mono text-xs"
                placeholder="Paste a previously exported JSON here…"
                value={importText}
                onChange={(e) => setImportText(e.target.value)}
              />
              <div className="flex gap-2">
                <button className="btn btn-primary btn-sm" onClick={handleImport}>
                  Import
                </button>
                <button className="btn btn-ghost btn-sm" onClick={() => setImportText('')}>
                  Clear
                </button>
              </div>
            </div>
          )}
        </div>
        <div className="modal-backdrop" onClick={() => onClose()} />
      </div>
    </>
  )
}
